import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { reportsService } from '../../../services/reportsService';
import { useAuth } from '../../../contexts/AuthContext';

const ReportViewer = ({ selectedReport, reportData }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [exportFormat, setExportFormat] = useState('pdf');
  const { user, organizationId } = useAuth();

  useEffect(() => {
    if (selectedReport && user) {
      loadReportData();
    }
  }, [selectedReport, user, organizationId]);

  const loadReportData = async () => {
    if (!selectedReport || !user) return;

    setLoading(true);
    setError('');

    try {
      if (!organizationId) {
        throw new Error('No organization ID found for user. Please check your account setup.');
      }
      
      const reportData = await reportsService?.getReportData(selectedReport?.id, organizationId);
      setData(reportData);
    } catch (error) {
      console.error('Failed to load report data:', error);
      setError(error?.message || 'Failed to load report data. Please try again.');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format) => {
    try {
      // TODO: Implement actual export functionality
      console.log(`Exporting report ${selectedReport?.id} as ${format}`);
      
      // Simulate export process
      const exportData = {
        reportName: selectedReport?.name,
        format: format,
        data: data,
        exportedAt: new Date()?.toISOString()
      };
      
      // For now, just log the export data
      console.log('Export data:', exportData);
      
      // Show success message
      alert(`Report exported as ${format?.toUpperCase()} successfully!`);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Please try again.');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleShare = () => {
    // TODO: Implement sharing functionality
    console.log('Sharing report:', selectedReport?.id);
    alert('Share functionality will be implemented to generate shareable links and email reports.');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    })?.format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const renderSalesSummary = (reportData) => {
    if (!reportData?.data) return null;

    return (
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-success/10 border border-success/20 rounded-lg p-4">
            <div className="flex items-center">
              <Icon name="TrendingUp" size={20} className="text-success mr-2" />
              <div>
                <p className="text-sm text-success font-medium">Total Sales</p>
                <p className="text-lg font-bold text-success">{formatCurrency(reportData?.totals?.totalSales)}</p>
              </div>
            </div>
          </div>
          <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
            <div className="flex items-center">
              <Icon name="DollarSign" size={20} className="text-primary mr-2" />
              <div>
                <p className="text-sm text-primary font-medium">Total Paid</p>
                <p className="text-lg font-bold text-primary">{formatCurrency(reportData?.totals?.totalPaid)}</p>
              </div>
            </div>
          </div>
          <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
            <div className="flex items-center">
              <Icon name="Clock" size={20} className="text-warning mr-2" />
              <div>
                <p className="text-sm text-warning font-medium">Outstanding</p>
                <p className="text-lg font-bold text-warning">{formatCurrency(reportData?.totals?.totalOutstanding)}</p>
              </div>
            </div>
          </div>
        </div>
        {/* Data Table */}
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/20">
                <tr>
                  <th className="text-left py-3 px-4 font-semibold text-foreground">Invoice #</th>
                  <th className="text-left py-3 px-4 font-semibold text-foreground">Customer</th>
                  <th className="text-left py-3 px-4 font-semibold text-foreground">Issue Date</th>
                  <th className="text-left py-3 px-4 font-semibold text-foreground">Due Date</th>
                  <th className="text-left py-3 px-4 font-semibold text-foreground">Amount</th>
                  <th className="text-left py-3 px-4 font-semibold text-foreground">Paid</th>
                  <th className="text-left py-3 px-4 font-semibold text-foreground">Balance</th>
                  <th className="text-left py-3 px-4 font-semibold text-foreground">Status</th>
                </tr>
              </thead>
              <tbody>
                {Array.isArray(reportData?.data) && reportData?.data?.map((invoice, index) => (
                  <tr key={index} className="border-b border-border hover:bg-muted/20">
                    <td className="py-3 px-4 font-medium text-foreground">{invoice?.invoiceNumber}</td>
                    <td className="py-3 px-4 text-foreground">{invoice?.customerName}</td>
                    <td className="py-3 px-4 text-muted-foreground">{formatDate(invoice?.issueDate)}</td>
                    <td className="py-3 px-4 text-muted-foreground">{formatDate(invoice?.dueDate)}</td>
                    <td className="py-3 px-4 text-foreground">{formatCurrency(invoice?.totalAmount)}</td>
                    <td className="py-3 px-4 text-foreground">{formatCurrency(invoice?.amountPaid)}</td>
                    <td className="py-3 px-4 text-foreground">{formatCurrency(invoice?.balanceDue)}</td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        invoice?.status === 'paid' ? 'bg-success/20 text-success' :
                        invoice?.status === 'overdue'? 'bg-error/20 text-error' : 'bg-warning/20 text-warning'
                      }`}>
                        {invoice?.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderExpenseSummary = (reportData) => {
    if (!reportData?.data) return null;

    return (
      <div className="space-y-6">
        {/* Summary */}
        <div className="bg-error/10 border border-error/20 rounded-lg p-4">
          <div className="flex items-center">
            <Icon name="ArrowDown" size={20} className="text-error mr-2" />
            <div>
              <p className="text-sm text-error font-medium">Total Expenses</p>
              <p className="text-lg font-bold text-error">{formatCurrency(reportData?.totals?.totalExpenses)}</p>
            </div>
          </div>
        </div>
        {/* Category Breakdown */}
        {reportData?.totals?.totalByCategory && (
          <div className="bg-card border border-border rounded-lg p-4">
            <h4 className="text-lg font-semibold text-foreground mb-4">Expenses by Category</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(reportData?.totals?.totalByCategory || {})?.map(([category, amount]) => (
                <div key={category} className="flex justify-between items-center p-2 bg-muted/10 rounded">
                  <span className="text-foreground capitalize">{category?.replace('_', ' ')}</span>
                  <span className="font-medium text-foreground">{formatCurrency(amount)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* Data Table */}
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/20">
                <tr>
                  <th className="text-left py-3 px-4 font-semibold text-foreground">Type</th>
                  <th className="text-left py-3 px-4 font-semibold text-foreground">Number</th>
                  <th className="text-left py-3 px-4 font-semibold text-foreground">Vendor</th>
                  <th className="text-left py-3 px-4 font-semibold text-foreground">Date</th>
                  <th className="text-left py-3 px-4 font-semibold text-foreground">Category</th>
                  <th className="text-left py-3 px-4 font-semibold text-foreground">Amount</th>
                  <th className="text-left py-3 px-4 font-semibold text-foreground">Status</th>
                </tr>
              </thead>
              <tbody>
                {Array.isArray(reportData?.data) && reportData?.data?.map((expense, index) => (
                  <tr key={index} className="border-b border-border hover:bg-muted/20">
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        expense?.type === 'Expense' ? 'bg-orange/20 text-orange' : 'bg-purple/20 text-purple'
                      }`}>
                        {expense?.type}
                      </span>
                    </td>
                    <td className="py-3 px-4 font-medium text-foreground">{expense?.number}</td>
                    <td className="py-3 px-4 text-foreground">{expense?.vendorName}</td>
                    <td className="py-3 px-4 text-muted-foreground">{formatDate(expense?.date)}</td>
                    <td className="py-3 px-4 text-foreground capitalize">{expense?.category?.replace('_', ' ')}</td>
                    <td className="py-3 px-4 text-foreground">{formatCurrency(expense?.amount)}</td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        expense?.status === 'approved' ? 'bg-success/20 text-success' :
                        expense?.status === 'rejected'? 'bg-error/20 text-error' : 'bg-warning/20 text-warning'
                      }`}>
                        {expense?.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderCashFlowStatement = (reportData) => {
    if (!reportData?.inflows && !reportData?.outflows) return null;

    return (
      <div className="space-y-6">
        {/* Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-success/10 border border-success/20 rounded-lg p-4">
            <div className="flex items-center">
              <Icon name="ArrowUp" size={20} className="text-success mr-2" />
              <div>
                <p className="text-sm text-success font-medium">Cash Inflows</p>
                <p className="text-lg font-bold text-success">{formatCurrency(reportData?.summary?.totalInflows)}</p>
              </div>
            </div>
          </div>
          <div className="bg-error/10 border border-error/20 rounded-lg p-4">
            <div className="flex items-center">
              <Icon name="ArrowDown" size={20} className="text-error mr-2" />
              <div>
                <p className="text-sm text-error font-medium">Cash Outflows</p>
                <p className="text-lg font-bold text-error">{formatCurrency(reportData?.summary?.totalOutflows)}</p>
              </div>
            </div>
          </div>
          <div className={`border rounded-lg p-4 ${
            reportData?.summary?.netCashFlow >= 0 ? 'bg-primary/10 border-primary/20' : 'bg-warning/10 border-warning/20'
          }`}>
            <div className="flex items-center">
              <Icon name="ArrowRightLeft" size={20} className={`mr-2 ${
                reportData?.summary?.netCashFlow >= 0 ? 'text-primary' : 'text-warning'
              }`} />
              <div>
                <p className={`text-sm font-medium ${
                  reportData?.summary?.netCashFlow >= 0 ? 'text-primary' : 'text-warning'
                }`}>Net Cash Flow</p>
                <p className={`text-lg font-bold ${
                  reportData?.summary?.netCashFlow >= 0 ? 'text-primary' : 'text-warning'
                }`}>{formatCurrency(reportData?.summary?.netCashFlow)}</p>
              </div>
            </div>
          </div>
        </div>
        {/* Cash Inflows */}
        {Array.isArray(reportData?.inflows) && reportData?.inflows?.length > 0 && (
          <div className="bg-card border border-border rounded-lg">
            <div className="p-4 border-b border-border">
              <h4 className="text-lg font-semibold text-foreground">Cash Inflows (Payments Received)</h4>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/20">
                  <tr>
                    <th className="text-left py-3 px-4 font-semibold text-foreground">Reference</th>
                    <th className="text-left py-3 px-4 font-semibold text-foreground">Customer</th>
                    <th className="text-left py-3 px-4 font-semibold text-foreground">Date</th>
                    <th className="text-left py-3 px-4 font-semibold text-foreground">Method</th>
                    <th className="text-left py-3 px-4 font-semibold text-foreground">Amount</th>
                    <th className="text-left py-3 px-4 font-semibold text-foreground">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData?.inflows?.map((payment, index) => (
                    <tr key={index} className="border-b border-border hover:bg-muted/20">
                      <td className="py-3 px-4 font-medium text-foreground">{payment?.paymentReference}</td>
                      <td className="py-3 px-4 text-foreground">{payment?.customerName}</td>
                      <td className="py-3 px-4 text-muted-foreground">{formatDate(payment?.date)}</td>
                      <td className="py-3 px-4 text-foreground capitalize">{payment?.method?.replace('_', ' ')}</td>
                      <td className="py-3 px-4 text-success font-medium">{formatCurrency(payment?.amount)}</td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          payment?.status === 'cleared' ? 'bg-success/20 text-success' :
                          payment?.status === 'bounced'? 'bg-error/20 text-error' : 'bg-warning/20 text-warning'
                        }`}>
                          {payment?.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {/* Cash Outflows */}
        {Array.isArray(reportData?.outflows) && reportData?.outflows?.length > 0 && (
          <div className="bg-card border border-border rounded-lg">
            <div className="p-4 border-b border-border">
              <h4 className="text-lg font-semibold text-foreground">Cash Outflows (Payments Made)</h4>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/20">
                  <tr>
                    <th className="text-left py-3 px-4 font-semibold text-foreground">Reference</th>
                    <th className="text-left py-3 px-4 font-semibold text-foreground">Vendor</th>
                    <th className="text-left py-3 px-4 font-semibold text-foreground">Date</th>
                    <th className="text-left py-3 px-4 font-semibold text-foreground">Method</th>
                    <th className="text-left py-3 px-4 font-semibold text-foreground">Amount</th>
                    <th className="text-left py-3 px-4 font-semibold text-foreground">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData?.outflows?.map((payment, index) => (
                    <tr key={index} className="border-b border-border hover:bg-muted/20">
                      <td className="py-3 px-4 font-medium text-foreground">{payment?.paymentReference}</td>
                      <td className="py-3 px-4 text-foreground">{payment?.vendorName}</td>
                      <td className="py-3 px-4 text-muted-foreground">{formatDate(payment?.date)}</td>
                      <td className="py-3 px-4 text-foreground capitalize">{payment?.method?.replace('_', ' ')}</td>
                      <td className="py-3 px-4 text-error font-medium">-{formatCurrency(payment?.amount)}</td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          payment?.status === 'cleared' ? 'bg-success/20 text-success' : 'bg-warning/20 text-warning'
                        }`}>
                          {payment?.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderOutstandingBalances = (reportData) => {
    if (!reportData?.data) return null;

    return (
      <div className="space-y-6">
        {/* Summary and Aging */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-card border border-border rounded-lg p-4">
            <h4 className="text-lg font-semibold text-foreground mb-4">Summary</h4>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total Outstanding:</span>
                <span className="font-bold text-foreground">{formatCurrency(reportData?.totals?.totalOutstanding)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total Invoices:</span>
                <span className="font-medium text-foreground">{reportData?.totals?.totalInvoices}</span>
              </div>
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg p-4">
            <h4 className="text-lg font-semibold text-foreground mb-4">Aging Analysis</h4>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-success">Current:</span>
                <span className="font-medium text-success">{formatCurrency(reportData?.aging?.current)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-warning">1-30 Days:</span>
                <span className="font-medium text-warning">{formatCurrency(reportData?.aging?.days1to30)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-orange">31-60 Days:</span>
                <span className="font-medium text-orange">{formatCurrency(reportData?.aging?.days31to60)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-error">61-90 Days:</span>
                <span className="font-medium text-error">{formatCurrency(reportData?.aging?.days61to90)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-red-600">90+ Days:</span>
                <span className="font-bold text-red-600">{formatCurrency(reportData?.aging?.days90plus)}</span>
              </div>
            </div>
          </div>
        </div>
        {/* Outstanding Invoices Table */}
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/20">
                <tr>
                  <th className="text-left py-3 px-4 font-semibold text-foreground">Invoice #</th>
                  <th className="text-left py-3 px-4 font-semibold text-foreground">Customer</th>
                  <th className="text-left py-3 px-4 font-semibold text-foreground">Issue Date</th>
                  <th className="text-left py-3 px-4 font-semibold text-foreground">Due Date</th>
                  <th className="text-left py-3 px-4 font-semibold text-foreground">Total</th>
                  <th className="text-left py-3 px-4 font-semibold text-foreground">Paid</th>
                  <th className="text-left py-3 px-4 font-semibold text-foreground">Balance</th>
                  <th className="text-left py-3 px-4 font-semibold text-foreground">Days Overdue</th>
                  <th className="text-left py-3 px-4 font-semibold text-foreground">Category</th>
                </tr>
              </thead>
              <tbody>
                {Array.isArray(reportData?.data) && reportData?.data?.map((invoice, index) => (
                  <tr key={index} className="border-b border-border hover:bg-muted/20">
                    <td className="py-3 px-4 font-medium text-foreground">{invoice?.invoiceNumber}</td>
                    <td className="py-3 px-4 text-foreground">{invoice?.customerName}</td>
                    <td className="py-3 px-4 text-muted-foreground">{formatDate(invoice?.issueDate)}</td>
                    <td className="py-3 px-4 text-muted-foreground">{formatDate(invoice?.dueDate)}</td>
                    <td className="py-3 px-4 text-foreground">{formatCurrency(invoice?.totalAmount)}</td>
                    <td className="py-3 px-4 text-foreground">{formatCurrency(invoice?.amountPaid)}</td>
                    <td className="py-3 px-4 font-medium text-foreground">{formatCurrency(invoice?.balanceDue)}</td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        invoice?.daysOverdue === 0 ? 'bg-success/20 text-success' :
                        invoice?.daysOverdue <= 30 ? 'bg-warning/20 text-warning' :
                        invoice?.daysOverdue <= 60 ? 'bg-orange/20 text-orange': 'bg-error/20 text-error'
                      }`}>
                        {invoice?.daysOverdue} days
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        invoice?.agingCategory === 'Current' ? 'bg-success/20 text-success' :
                        invoice?.agingCategory === '1-30 Days' ? 'bg-warning/20 text-warning' :
                        invoice?.agingCategory === '31-60 Days'? 'bg-orange/20 text-orange' : 'bg-error/20 text-error'
                      }`}>
                        {invoice?.agingCategory}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderTrialBalance = (reportData) => {
    if (!reportData?.data) return null;

    return (
      <div className="space-y-6">
        {/* Enhanced Summary Cards with Direct Expense Integration */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-success/10 border border-success/20 rounded-lg p-4">
            <div className="flex items-center">
              <Icon name="Plus" size={20} className="text-success mr-2" />
              <div>
                <p className="text-sm text-success font-medium">Total Debits</p>
                <p className="text-lg font-bold text-success">{formatCurrency(reportData?.totals?.totalDebits)}</p>
                {reportData?.totals?.accountsWithActivity > 0 && (
                  <p className="text-xs text-success/70 mt-1">
                    {reportData?.totals?.accountsWithActivity} active accounts
                  </p>
                )}
              </div>
            </div>
          </div>
          <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
            <div className="flex items-center">
              <Icon name="Minus" size={20} className="text-primary mr-2" />
              <div>
                <p className="text-sm text-primary font-medium">Total Credits</p>
                <p className="text-lg font-bold text-primary">{formatCurrency(reportData?.totals?.totalCredits)}</p>
                <p className="text-xs text-primary/70 mt-1">
                  {reportData?.totals?.totalAccounts} total accounts
                </p>
              </div>
            </div>
          </div>
          <div className={`border rounded-lg p-4 ${
            reportData?.totals?.isBalanced ? 'bg-success/10 border-success/20' : 'bg-error/10 border-error/20'
          }`}>
            <div className="flex items-center">
              <Icon 
                name={reportData?.totals?.isBalanced ? "CheckCircle" : "AlertCircle"} 
                size={20} 
                className={`mr-2 ${reportData?.totals?.isBalanced ? 'text-success' : 'text-error'}`} 
              />
              <div>
                <p className={`text-sm font-medium ${
                  reportData?.totals?.isBalanced ? 'text-success' : 'text-error'
                }`}>
                  Balance Status
                </p>
                <p className={`text-lg font-bold ${
                  reportData?.totals?.isBalanced ? 'text-success' : 'text-error'
                }`}>
                  {reportData?.totals?.isBalanced ? 'Balanced' : 'Out of Balance'}
                </p>
                {!reportData?.totals?.isBalanced && (
                  <p className="text-xs text-error mt-1">
                    Diff: {formatCurrency(Math.abs(reportData?.totals?.difference))}
                  </p>
                )}
              </div>
            </div>
          </div>
          <div className="bg-orange/10 border border-orange/20 rounded-lg p-4">
            <div className="flex items-center">
              <Icon name="Receipt" size={20} className="text-orange mr-2" />
              <div>
                <p className="text-sm text-orange font-medium">Expense Accounts</p>
                <p className="text-lg font-bold text-orange">{formatCurrency(reportData?.totals?.totalExpenseAmount || 0)}</p>
                <p className="text-xs text-orange/70 mt-1">
                  {reportData?.totals?.expenseAccountsCount || 0} expense accounts
                </p>
              </div>
            </div>
          </div>
        </div>
        {/* Enhanced Expense Integration Status */}
        {reportData?.totals?.enhancedWithExpenseData && (
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-semibold text-foreground flex items-center">
                <Icon name="Zap" size={18} className="mr-2 text-accent" />
                Enhanced Expense Integration
              </h4>
              <div className="flex items-center space-x-2 text-xs">
                <Icon name="CheckCircle2" size={16} className="text-accent" />
                <span className="text-accent font-medium">
                  {reportData?.totals?.dataIntegrationStatus === 'enhanced_integration' ? 'Full Integration' : 'Partial Integration'}
                </span>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Direct Expense Integration */}
              <div className="bg-orange/5 border border-orange/20 rounded-lg p-3">
                <div className="flex items-center mb-2">
                  <Icon name="CreditCard" size={16} className="text-orange mr-2" />
                  <h5 className="text-sm font-medium text-foreground">Direct Expenses</h5>
                </div>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Expense Accounts:</span>
                    <span className="font-medium text-foreground">{reportData?.totals?.expenseAccountsCount || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total Expense Value:</span>
                    <span className="font-medium text-orange">{formatCurrency(reportData?.totals?.totalExpenseAmount || 0)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Integration:</span>
                    <span className="font-medium text-accent">
                      {reportData?.validation?.integrationCompleteness || 'Unknown'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Bill Integration */}
              <div className="bg-red/5 border border-red/20 rounded-lg p-3">
                <div className="flex items-center mb-2">
                  <Icon name="FileText" size={16} className="text-red mr-2" />
                  <h5 className="text-sm font-medium text-foreground">Bills Integration</h5>
                </div>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Bill Accounts:</span>
                    <span className="font-medium text-foreground">{reportData?.totals?.billAccountsCount || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Total Bill Value:</span>
                    <span className="font-medium text-red">{formatCurrency(reportData?.totals?.totalBillAmount || 0)}</span>
                  </div>
                </div>
              </div>

              {/* Integration Health */}
              <div className="bg-accent/5 border border-accent/20 rounded-lg p-3">
                <div className="flex items-center mb-2">
                  <Icon name="Activity" size={16} className="text-accent mr-2" />
                  <h5 className="text-sm font-medium text-foreground">Integration Health</h5>
                </div>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Mapped Expenses:</span>
                    <span className="font-medium text-foreground">{reportData?.validation?.expensesWithAccountMapping || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Unmapped:</span>
                    <span className={`font-medium ${(reportData?.validation?.expensesWithoutAccountMapping || 0) > 0 ? 'text-warning' : 'text-success'}`}>
                      {reportData?.validation?.expensesWithoutAccountMapping || 0}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Method:</span>
                    <span className="font-medium text-accent text-[10px]">
                      {reportData?.metadata?.expenseIntegrationMethod === 'direct_database_calculation' ? 'Direct DB' : 'Virtual'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Validation Recommendations */}
            {reportData?.validation?.expensesWithoutAccountMapping > 0 && (
              <div className="mt-4 bg-warning/5 border border-warning/20 rounded-lg p-3">
                <div className="flex items-start space-x-2">
                  <Icon name="AlertTriangle" size={16} className="text-warning mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-warning">Expense Mapping Recommendation</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {reportData?.validation?.recommendedActions}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        {/* Detailed Expense Account Breakdown */}
        {reportData?.expenseDetails && reportData?.expenseDetails?.length > 0 && (
          <div className="bg-card border border-border rounded-lg">
            <div className="p-4 border-b border-border">
              <div className="flex items-center justify-between">
                <h4 className="text-lg font-semibold text-foreground flex items-center">
                  <Icon name="BarChart3" size={18} className="mr-2" />
                  Expense Account Details
                </h4>
                <div className="text-xs text-muted-foreground">
                  {reportData?.expenseDetails?.length} expense accounts with transactions
                </div>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/20">
                  <tr>
                    <th className="text-left py-3 px-4 font-semibold text-foreground">Account Code</th>
                    <th className="text-left py-3 px-4 font-semibold text-foreground">Account Name</th>
                    <th className="text-right py-3 px-4 font-semibold text-foreground">Expense Amount</th>
                    <th className="text-right py-3 px-4 font-semibold text-foreground">Transaction Count</th>
                    <th className="text-right py-3 px-4 font-semibold text-foreground">Average Amount</th>
                    <th className="text-left py-3 px-4 font-semibold text-foreground">Categories</th>
                    <th className="text-left py-3 px-4 font-semibold text-foreground">Latest Date</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData?.expenseDetails?.map((detail, index) => (
                    <tr key={index} className="border-b border-border hover:bg-muted/20">
                      <td className="py-3 px-4 font-medium text-foreground">{detail?.accountCode}</td>
                      <td className="py-3 px-4 text-foreground">{detail?.accountName}</td>
                      <td className="py-3 px-4 text-right font-medium text-orange">
                        {formatCurrency(detail?.totalExpenseAmount)}
                      </td>
                      <td className="py-3 px-4 text-right text-foreground">{detail?.expenseCount}</td>
                      <td className="py-3 px-4 text-right text-muted-foreground">
                        {formatCurrency(detail?.averageExpense)}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex flex-wrap gap-1">
                          {detail?.expenseCategories?.slice(0, 2)?.map((category, catIndex) => (
                            <span 
                              key={catIndex}
                              className="px-1 py-0.5 text-xs bg-orange/10 text-orange rounded"
                            >
                              {category?.replace('_', ' ')}
                            </span>
                          ))}
                          {detail?.expenseCategories?.length > 2 && (
                            <span className="text-xs text-muted-foreground">
                              +{detail?.expenseCategories?.length - 2} more
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-muted-foreground">
                        {formatDate(detail?.latestExpenseDate)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {/* Calculation Method and Metadata */}
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-lg font-semibold text-foreground">Enhanced Calculation Details</h4>
              <div className="flex items-center space-x-6 text-sm text-muted-foreground mt-2">
                <div className="flex items-center space-x-2">
                  <Icon name="Calculator" size={16} />
                  <span>Method: {reportData?.calculationMethod || 'Current Balance'}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="Calendar" size={16} />
                  <span>As of: {formatDate(reportData?.asOfDate)}</span>
                </div>
                {reportData?.metadata?.generatedAt && (
                  <div className="flex items-center space-x-2">
                    <Icon name="Clock" size={16} />
                    <span>Generated: {formatDate(reportData?.metadata?.generatedAt)}</span>
                  </div>
                )}
                {reportData?.totals?.enhancedWithExpenseData && (
                  <div className="flex items-center space-x-2">
                    <Icon name="Zap" size={16} className="text-accent" />
                    <span className="text-accent">Enhanced with Direct Expenses</span>
                  </div>
                )}
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={loadReportData}
              iconName="RefreshCw"
              title="Refresh trial balance with latest expense data"
            >
              Refresh
            </Button>
          </div>
        </div>
        {/* Account Type Summaries */}
        {reportData?.typeSummaries && Object.keys(reportData?.typeSummaries)?.length > 0 && (
          <div className="bg-card border border-border rounded-lg">
            <div className="p-4 border-b border-border">
              <h4 className="text-lg font-semibold text-foreground">Summary by Account Type</h4>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(reportData?.typeSummaries || {})?.map(([type, summary]) => (
                  <div key={type} className="bg-muted/5 border border-muted/20 rounded-lg p-4">
                    <div className="text-center">
                      <p className="text-sm font-medium text-muted-foreground mb-2 uppercase">
                        {type?.replace('_', ' ')}
                      </p>
                      <div className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className="text-success">Debits:</span>
                          <span className="font-medium text-success">{formatCurrency(summary?.totalDebit || 0)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-primary">Credits:</span>
                          <span className="font-medium text-primary">{formatCurrency(summary?.totalCredit || 0)}</span>
                        </div>
                        <div className="flex justify-between text-xs border-t border-muted/20 pt-1">
                          <span className="text-muted-foreground">Accounts:</span>
                          <span className="text-muted-foreground">{summary?.accountCount || 0}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
        {/* Enhanced Trial Balance Table with Expense Integration */}
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="p-4 border-b border-border">
            <div className="flex items-center justify-between">
              <h4 className="text-lg font-semibold text-foreground">Account Balances (Enhanced with Expenses)</h4>
              <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                <span>As of {formatDate(reportData?.asOfDate)}</span>
                {reportData?.metadata?.includesJournalTransactions && (
                  <div className="flex items-center space-x-1">
                    <Icon name="Zap" size={14} className="text-accent" />
                    <span className="text-accent">Live Calculations</span>
                  </div>
                )}
                {reportData?.totals?.enhancedWithExpenseData && (
                  <div className="flex items-center space-x-1">
                    <Icon name="CreditCard" size={14} className="text-orange" />
                    <span className="text-orange">Direct Expenses</span>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/20">
                <tr>
                  <th className="text-left py-3 px-4 font-semibold text-foreground">Account Code</th>
                  <th className="text-left py-3 px-4 font-semibold text-foreground">Account Name</th>
                  <th className="text-left py-3 px-4 font-semibold text-foreground">Account Type</th>
                  <th className="text-left py-3 px-4 font-semibold text-foreground">Data Integration</th>
                  <th className="text-right py-3 px-4 font-semibold text-foreground">Opening Balance</th>
                  <th className="text-right py-3 px-4 font-semibold text-foreground">Debit Balance</th>
                  <th className="text-right py-3 px-4 font-semibold text-foreground">Credit Balance</th>
                  <th className="text-right py-3 px-4 font-semibold text-foreground">Expense Value</th>
                  <th className="text-right py-3 px-4 font-semibold text-foreground">Period Activity</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(reportData?.accountsByType || {})?.map(([accountType, accounts]) => (
                  <React.Fragment key={accountType}>
                    {/* Account Type Header with Enhanced Summary */}
                    <tr className="bg-muted/10">
                      <td colSpan="6" className="py-2 px-4 font-semibold text-foreground uppercase text-sm">
                        {accountType?.replace('_', ' ')} ({Array.isArray(accounts) ? accounts?.length : 0} accounts)
                        {accountType === 'expense' && (
                          <span className="ml-2 text-orange text-xs font-normal">
                            - {reportData?.typeSummaries?.[accountType]?.expenseCount || 0} expense transactions
                          </span>
                        )}
                      </td>
                      <td className="py-2 px-4 text-right font-semibold text-success text-sm">
                        {formatCurrency(reportData?.typeSummaries?.[accountType]?.totalDebit || 0)}
                      </td>
                      <td className="py-2 px-4 text-right font-semibold text-primary text-sm">
                        {formatCurrency(reportData?.typeSummaries?.[accountType]?.totalCredit || 0)}
                      </td>
                      <td className="py-2 px-4 text-right font-semibold text-orange text-sm">
                        {accountType === 'expense' ? formatCurrency(reportData?.typeSummaries?.[accountType]?.totalDebit || 0) : '-'}
                      </td>
                      <td className="py-2 px-4 text-right font-semibold text-accent text-sm">
                        {formatCurrency(reportData?.typeSummaries?.[accountType]?.totalActivity || 0)}
                      </td>
                    </tr>
                    {/* Account Rows with Enhanced Expense Display */}
                    {Array.isArray(accounts) && accounts?.map((account, index) => (
                      <tr key={`${accountType}-${index}`} className={`border-b border-border hover:bg-muted/20 ${
                        account?.isEnhancedWithExpenses ? 'bg-orange/5 border-l-2 border-l-orange' : 
                        account?.source !== 'journal_entries' ? 'bg-accent/5' : ''
                      }`}>
                        <td className="py-3 px-4 font-medium text-foreground">{account?.accountCode}</td>
                        <td className="py-3 px-4">
                          <div className="flex items-center space-x-2">
                            <span className="text-foreground">{account?.accountName}</span>
                            {account?.isEnhancedWithExpenses && (
                              <div className="flex items-center space-x-1">
                                <Icon name="Receipt" size={12} className="text-orange" />
                                <span className="text-xs px-1 py-0.5 bg-orange/10 text-orange rounded">
                                  +Expenses
                                </span>
                              </div>
                            )}
                            {account?.source !== 'journal_entries' && account?.source !== 'current_balance_only' && (
                              <div className="flex items-center space-x-1">
                                <Icon name="Star" size={12} className="text-accent" />
                                <span className="text-xs px-1 py-0.5 bg-accent/10 text-accent rounded">Enhanced</span>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            account?.accountType === 'asset' ? 'bg-blue/20 text-blue' :
                            account?.accountType === 'liability' ? 'bg-red/20 text-red' :
                            account?.accountType === 'equity' ? 'bg-green/20 text-green' :
                            account?.accountType === 'revenue' ? 'bg-purple/20 text-purple' :
                            account?.accountType === 'expense' ? 'bg-orange/20 text-orange' : 'bg-gray/20 text-gray'
                          }`}>
                            {account?.accountType?.charAt(0)?.toUpperCase() + account?.accountType?.slice(1)}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex flex-col space-y-1">
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              (account?.source || '')?.includes('transactions') ? 'bg-orange/10 text-orange' :
                              account?.source === 'journal_entries_only' ? 'bg-gray/10 text-gray' :
                              account?.source === 'current_balance_only' ? 'bg-blue/10 text-blue' : 'bg-gray/10 text-gray'
                            }`}>
                              {(account?.source || '')?.includes('transactions') ? 'Enhanced' :
                               account?.source === 'journal_entries_only' ? 'Journal' :
                               account?.source === 'current_balance_only' ? 'Balance' : 'Standard'}
                            </span>
                            {(account?.expenseCount > 0 || account?.billCount > 0) && (
                              <div className="text-xs text-muted-foreground">
                                {account?.expenseCount > 0 && (
                                  <span className="text-orange">{account?.expenseCount}E</span>
                                )}
                                {account?.expenseCount > 0 && account?.billCount > 0 && <span> • </span>}
                                {account?.billCount > 0 && (
                                  <span className="text-red">{account?.billCount}B</span>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-right font-medium text-muted-foreground">
                          {account?.openingBalance !== undefined ? formatCurrency(account?.openingBalance) : '-'}
                        </td>
                        <td className="py-3 px-4 text-right font-medium">
                          {account?.debitBalance > 0 ? (
                            <div className="flex flex-col">
                              <span className="text-success">{formatCurrency(account?.debitBalance)}</span>
                              {account?.accountType === 'expense' && account?.expenseCount > 0 && (
                                <span className="text-xs text-orange">
                                  (includes {account?.expenseCount} expenses)
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-right font-medium">
                          {account?.creditBalance > 0 ? (
                            <div className="flex flex-col">
                              <span className="text-primary">{formatCurrency(account?.creditBalance)}</span>
                              {account?.accountType === 'liability' && account?.billCount > 0 && (
                                <span className="text-xs text-red">
                                  (includes {account?.billCount} bills)
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-right font-medium">
                          {account?.accountType === 'expense' && account?.debitBalance > 0 ? (
                            <span className="text-orange font-semibold">
                              {formatCurrency(account?.debitBalance)}
                            </span>
                          ) : account?.accountType === 'liability' && account?.creditBalance > 0 ? (
                            <span className="text-red font-semibold">
                              {formatCurrency(account?.creditBalance)}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-right font-medium">
                          {account?.periodActivity !== undefined ? (
                            <span className={`${
                              account?.periodActivity > 0 ? 'text-accent' : 'text-muted-foreground'
                            }`}>
                              {formatCurrency(Math.abs(account?.periodActivity))}
                            </span>
                          ) : '-'}
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
                {/* Enhanced Totals Row with Expense Totals */}
                <tr className="bg-muted/20 border-t-2 border-border">
                  <td colSpan="5" className="py-3 px-4 font-bold text-foreground">
                    TOTALS
                    {reportData?.totals?.expenseAccountsCount > 0 && (
                      <span className="ml-2 text-orange font-normal text-sm">
                        ({reportData?.totals?.expenseAccountsCount} expense accounts integrated)
                      </span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-right font-bold text-success">
                    {formatCurrency(reportData?.totals?.totalDebits)}
                  </td>
                  <td className="py-3 px-4 text-right font-bold text-primary">
                    {formatCurrency(reportData?.totals?.totalCredits)}
                  </td>
                  <td className="py-3 px-4 text-right font-bold text-orange">
                    {formatCurrency(reportData?.totals?.totalExpenseAmount || 0)}
                  </td>
                  <td className="py-3 px-4 text-right font-bold text-accent">
                    {formatCurrency(reportData?.totals?.totalNetActivity || 0)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        {/* Enhanced Balance Verification with Expense Information */}
        {!reportData?.totals?.isBalanced && (
          <div className="bg-error/5 border border-error/20 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Icon name="AlertTriangle" size={20} className="text-error mt-0.5" />
              <div>
                <h4 className="text-sm font-semibold text-error mb-1">Trial Balance Out of Balance</h4>
                <p className="text-sm text-muted-foreground mb-2">
                  The enhanced trial balance does not balance. This may indicate issues with expense account mappings, 
                  journal entries, or missing integration between expenses and chart of accounts.
                </p>
                <div className="space-y-1">
                  <p className="text-sm text-error font-medium">
                    Difference: {formatCurrency(Math.abs(reportData?.totals?.difference))} 
                    ({reportData?.totals?.difference > 0 ? 'Debits exceed Credits' : 'Credits exceed Debits'})
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Calculation Method: {reportData?.calculationMethod}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Total Accounts: {reportData?.totals?.totalAccounts}, 
                    Active Accounts: {reportData?.totals?.accountsWithActivity}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Expense Integration: {reportData?.totals?.expenseAccountsCount} accounts, 
                    Total: {formatCurrency(reportData?.totals?.totalExpenseAmount || 0)}
                  </p>
                  {reportData?.validation && (
                    <p className="text-xs text-muted-foreground">
                      Mapping Status: {reportData?.validation?.expensesWithAccountMapping}/{reportData?.validation?.totalExpensesInDatabase} expenses mapped
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Enhanced Balance Success Message */}
        {reportData?.totals?.isBalanced && (
          <div className="bg-success/5 border border-success/20 rounded-lg p-4">
            <div className="flex items-center space-x-3">
              <Icon name="CheckCircle" size={20} className="text-success" />
              <div>
                <h4 className="text-sm font-semibold text-success mb-1">Enhanced Trial Balance is Balanced ✓</h4>
                <p className="text-sm text-muted-foreground">
                  All accounts are properly balanced with debits equaling credits. 
                  Your accounting records including direct expense integration are mathematically sound.
                </p>
                <p className="text-xs text-success mt-1">
                  Method: {reportData?.calculationMethod} | 
                  Accounts: {reportData?.totals?.totalAccounts} | 
                  Active: {reportData?.totals?.accountsWithActivity} |
                  Expenses: {formatCurrency(reportData?.totals?.totalExpenseAmount || 0)} 
                  ({reportData?.totals?.expenseAccountsCount || 0} accounts)
                </p>
              </div>
            </div>
          </div>
        )}
        {/* Report Actions */}
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center justify-between">
            <h4 className="text-lg font-semibold text-foreground">Report Actions</h4>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                iconName="FileSpreadsheet"
                onClick={() => handleExport('excel')}
              >
                Export to Excel
              </Button>
              <Button
                variant="outline"
                size="sm"
                iconName="FileText"
                onClick={() => handleExport('pdf')}
              >
                Export to PDF
              </Button>
              <Button
                variant="outline"
                size="sm"
                iconName="Mail"
                onClick={handleShare}
              >
                Email Report
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderProfitLossStatement = (reportData) => {
    if (!reportData?.revenue && !reportData?.expenses) return null;

    return (
      <div className="space-y-6">
        {/* Period and Summary */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-card border border-border rounded-lg p-4">
            <h4 className="text-lg font-semibold text-foreground mb-4">Report Period</h4>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Start Date:</span>
                <span className="font-medium text-foreground">{formatDate(reportData?.period?.startDate)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">End Date:</span>
                <span className="font-medium text-foreground">{formatDate(reportData?.period?.endDate)}</span>
              </div>
              {reportData?.summary?.calculationMethod && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Method:</span>
                  <span className="font-medium text-accent text-xs">
                    {reportData?.summary?.calculationMethod === 'enhanced_account_level_with_actual_values' ? 'Account-Level Actuals' : 'Standard'}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className={`border rounded-lg p-4 ${
            reportData?.summary?.isProfitable ? 'bg-success/10 border-success/20' : 'bg-error/10 border-error/20'
          }`}>
            <div className="flex items-center">
              <Icon 
                name={reportData?.summary?.isProfitable ? "TrendingUp" : "TrendingDown"} 
                size={20} 
                className={`mr-2 ${reportData?.summary?.isProfitable ? 'text-success' : 'text-error'}`} 
              />
              <div>
                <p className={`text-sm font-medium ${
                  reportData?.summary?.isProfitable ? 'text-success' : 'text-error'
                }`}>Net Income</p>
                <p className={`text-xl font-bold ${
                  reportData?.summary?.isProfitable ? 'text-success' : 'text-error'
                }`}>{formatCurrency(reportData?.summary?.netIncome)}</p>
                {reportData?.profitability?.netProfitMargin !== undefined && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {reportData?.profitability?.netProfitMargin?.toFixed(1)}% margin
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Account-Level Revenue Section */}
        <div className="bg-card border border-border rounded-lg">
          <div className="p-4 border-b border-border">
            <div className="flex items-center justify-between">
              <h4 className="text-lg font-semibold text-foreground flex items-center">
                <Icon name="TrendingUp" size={18} className="mr-2 text-success" />
                Revenue - All Account Actual Values
              </h4>
              <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                <span>{reportData?.revenue?.accountCount || 0} accounts</span>
                <span>{reportData?.revenue?.transactionCount || 0} transactions</span>
                {reportData?.metadata?.includesActualAccountValues && (
                  <div className="flex items-center space-x-1">
                    <Icon name="CheckCircle2" size={14} className="text-success" />
                    <span className="text-success">Actual Values</span>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Revenue Account Details Table */}
          {reportData?.accountDetails?.revenue && reportData?.accountDetails?.revenue?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/20">
                  <tr>
                    <th className="text-left py-3 px-4 font-semibold text-foreground">Account Code</th>
                    <th className="text-left py-3 px-4 font-semibold text-foreground">Account Name</th>
                    <th className="text-left py-3 px-4 font-semibold text-foreground">Account Subtype</th>
                    <th className="text-right py-3 px-4 font-semibold text-foreground">Actual Amount</th>
                    <th className="text-right py-3 px-4 font-semibold text-foreground">Transactions</th>
                    <th className="text-right py-3 px-4 font-semibold text-foreground">Avg Amount</th>
                    <th className="text-right py-3 px-4 font-semibold text-foreground">% of Total</th>
                    <th className="text-left py-3 px-4 font-semibold text-foreground">Data Sources</th>
                    <th className="text-left py-3 px-4 font-semibold text-foreground">IFRS Classification</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData?.accountDetails?.revenue?.map((account, index) => (
                    <tr key={index} className="border-b border-border hover:bg-muted/20">
                      <td className="py-3 px-4 font-medium text-foreground">{account?.accountCode}</td>
                      <td className="py-3 px-4 text-foreground">{account?.accountName}</td>
                      <td className="py-3 px-4">
                        <span className="px-2 py-1 text-xs bg-success/10 text-success rounded-full">
                          {account?.accountSubtype?.replace('_', ' ')?.toUpperCase()}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right font-bold text-success">
                        {formatCurrency(account?.actualAmount)}
                      </td>
                      <td className="py-3 px-4 text-right text-foreground">{account?.transactionCount}</td>
                      <td className="py-3 px-4 text-right text-muted-foreground">
                        {formatCurrency(account?.averageAmount)}
                      </td>
                      <td className="py-3 px-4 text-right text-foreground">
                        {account?.percentageOfTotal?.toFixed(1)}%
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex flex-wrap gap-1">
                          {account?.dataSources?.map((source, srcIndex) => (
                            <span 
                              key={srcIndex}
                              className={`px-1 py-0.5 text-xs rounded ${
                                source === 'invoices' ? 'bg-blue/10 text-blue' :
                                source === 'journal_entries'? 'bg-purple/10 text-purple' : 'bg-gray/10 text-gray'
                              }`}
                            >
                              {source}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-xs text-muted-foreground">
                        {account?.ifrsSection && (
                          <div>
                            <div>{account?.ifrsSection}</div>
                            {account?.ifrsSubsection && (
                              <div className="text-muted-foreground/70">{account?.ifrsSubsection}</div>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                  {/* Revenue Total Row */}
                  <tr className="bg-success/5 border-t-2 border-success/20">
                    <td colSpan="3" className="py-3 px-4 font-bold text-success">
                      TOTAL REVENUE ({reportData?.revenue?.accountCount} accounts)
                    </td>
                    <td className="py-3 px-4 text-right font-bold text-success text-lg">
                      {formatCurrency(reportData?.revenue?.totalRevenue)}
                    </td>
                    <td className="py-3 px-4 text-right font-bold text-success">
                      {reportData?.revenue?.transactionCount}
                    </td>
                    <td colSpan="4" className="py-3 px-4 text-right text-success font-medium">
                      100% of Revenue
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-4 text-center text-muted-foreground">
              <Icon name="TrendingUp" size={24} className="mx-auto mb-2" />
              <p>No revenue accounts with transactions found for this period</p>
            </div>
          )}
        </div>

        {/* Enhanced Account-Level Expense Section */}
        <div className="bg-card border border-border rounded-lg">
          <div className="p-4 border-b border-border">
            <div className="flex items-center justify-between">
              <h4 className="text-lg font-semibold text-foreground flex items-center">
                <Icon name="TrendingDown" size={18} className="mr-2 text-error" />
                Expenses - All Account Actual Values
              </h4>
              <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                <span>{reportData?.expenses?.accountCount || 0} accounts</span>
                <span>{reportData?.expenses?.transactionCount || 0} transactions</span>
                {reportData?.metadata?.includesActualAccountValues && (
                  <div className="flex items-center space-x-1">
                    <Icon name="CheckCircle2" size={14} className="text-success" />
                    <span className="text-success">Actual Values</span>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Expense Account Details Table */}
          {reportData?.accountDetails?.expense && reportData?.accountDetails?.expense?.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/20">
                  <tr>
                    <th className="text-left py-3 px-4 font-semibold text-foreground">Account Code</th>
                    <th className="text-left py-3 px-4 font-semibold text-foreground">Account Name</th>
                    <th className="text-left py-3 px-4 font-semibold text-foreground">Account Subtype</th>
                    <th className="text-right py-3 px-4 font-semibold text-foreground">Actual Amount</th>
                    <th className="text-right py-3 px-4 font-semibold text-foreground">Transactions</th>
                    <th className="text-right py-3 px-4 font-semibold text-foreground">Avg Amount</th>
                    <th className="text-right py-3 px-4 font-semibold text-foreground">% of Total</th>
                    <th className="text-left py-3 px-4 font-semibold text-foreground">Data Sources</th>
                    <th className="text-left py-3 px-4 font-semibold text-foreground">IFRS Classification</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData?.accountDetails?.expense?.map((account, index) => (
                    <tr key={index} className="border-b border-border hover:bg-muted/20">
                      <td className="py-3 px-4 font-medium text-foreground">{account?.accountCode}</td>
                      <td className="py-3 px-4 text-foreground">{account?.accountName}</td>
                      <td className="py-3 px-4">
                        <span className="px-2 py-1 text-xs bg-error/10 text-error rounded-full">
                          {account?.accountSubtype?.replace('_', ' ')?.toUpperCase()}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right font-bold text-error">
                        {formatCurrency(account?.actualAmount)}
                      </td>
                      <td className="py-3 px-4 text-right text-foreground">{account?.transactionCount}</td>
                      <td className="py-3 px-4 text-right text-muted-foreground">
                        {formatCurrency(account?.averageAmount)}
                      </td>
                      <td className="py-3 px-4 text-right text-foreground">
                        {account?.percentageOfTotal?.toFixed(1)}%
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex flex-wrap gap-1">
                          {account?.dataSources?.map((source, srcIndex) => (
                            <span 
                              key={srcIndex}
                              className={`px-1 py-0.5 text-xs rounded ${
                                source === 'expenses' ? 'bg-orange/10 text-orange' :
                                source === 'bills' ? 'bg-red/10 text-red' :
                                source === 'journal_entries'? 'bg-purple/10 text-purple' : 'bg-gray/10 text-gray'
                              }`}
                            >
                              {source}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-xs text-muted-foreground">
                        {account?.ifrsSection && (
                          <div>
                            <div>{account?.ifrsSection}</div>
                            {account?.ifrsSubsection && (
                              <div className="text-muted-foreground/70">{account?.ifrsSubsection}</div>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                  {/* Expense Total Row */}
                  <tr className="bg-error/5 border-t-2 border-error/20">
                    <td colSpan="3" className="py-3 px-4 font-bold text-error">
                      TOTAL EXPENSES ({reportData?.expenses?.accountCount} accounts)
                    </td>
                    <td className="py-3 px-4 text-right font-bold text-error text-lg">
                      {formatCurrency(reportData?.expenses?.totalExpenses)}
                    </td>
                    <td className="py-3 px-4 text-right font-bold text-error">
                      {reportData?.expenses?.transactionCount}
                    </td>
                    <td colSpan="4" className="py-3 px-4 text-right text-error font-medium">
                      100% of Expenses
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-4 text-center text-muted-foreground">
              <Icon name="TrendingDown" size={24} className="mx-auto mb-2" />
              <p>No expense accounts with transactions found for this period</p>
            </div>
          )}
        </div>

        {/* Enhanced Account Summary Analytics */}
        {reportData?.accountDetails && (
          <div className="bg-card border border-border rounded-lg">
            <div className="p-4 border-b border-border">
              <h4 className="text-lg font-semibold text-foreground flex items-center">
                <Icon name="BarChart3" size={18} className="mr-2 text-accent" />
                Account Analysis Summary
              </h4>
            </div>
            <div className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Revenue Account Analysis */}
                <div className="space-y-3">
                  <h5 className="font-medium text-foreground">Revenue Account Analysis</h5>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Active Revenue Accounts:</span>
                      <span className="font-medium text-foreground">{reportData?.revenue?.accountCount || 0}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Total Revenue Transactions:</span>
                      <span className="font-medium text-foreground">{reportData?.revenue?.transactionCount || 0}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Largest Revenue Account:</span>
                      <span className="font-medium text-success text-xs">{reportData?.revenue?.largestAccount}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Average per Account:</span>
                      <span className="font-medium text-success">
                        {reportData?.revenue?.accountCount > 0 
                          ? formatCurrency(reportData?.revenue?.totalRevenue / reportData?.revenue?.accountCount)
                          : '$0.00'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Expense Account Analysis */}
                <div className="space-y-3">
                  <h5 className="font-medium text-foreground">Expense Account Analysis</h5>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Active Expense Accounts:</span>
                      <span className="font-medium text-foreground">{reportData?.expenses?.accountCount || 0}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Total Expense Transactions:</span>
                      <span className="font-medium text-foreground">{reportData?.expenses?.transactionCount || 0}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Largest Expense Account:</span>
                      <span className="font-medium text-error text-xs">{reportData?.expenses?.largestAccount}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Average per Account:</span>
                      <span className="font-medium text-error">
                        {reportData?.expenses?.accountCount > 0 
                          ? formatCurrency(reportData?.expenses?.totalExpenses / reportData?.expenses?.accountCount)
                          : '$0.00'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Enhanced Profitability Metrics with Account Context */}
        <div className="bg-card border border-border rounded-lg">
          <div className="p-4 border-b border-border">
            <div className="flex items-center justify-between">
              <h4 className="text-lg font-semibold text-foreground">Profitability Analysis (Account-Based)</h4>
              {reportData?.metadata?.enhancementLevel && (
                <div className="flex items-center space-x-2">
                  <Icon name="Zap" size={14} className="text-accent" />
                  <span className="text-xs text-accent">Enhanced Calculations</span>
                </div>
              )}
            </div>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-1">Gross Profit</p>
                <p className="text-lg font-bold text-foreground">{formatCurrency(reportData?.profitability?.grossProfit)}</p>
                <p className="text-xs text-muted-foreground">
                  {reportData?.profitability?.grossProfitMargin?.toFixed(1)}% margin
                </p>
                <p className="text-xs text-accent mt-1">
                  Based on {reportData?.revenue?.accountCount || 0} revenue accounts
                </p>
              </div>
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-1">Operating Income</p>
                <p className="text-lg font-bold text-foreground">{formatCurrency(reportData?.profitability?.operatingIncome)}</p>
                <p className="text-xs text-muted-foreground">
                  {reportData?.profitability?.operatingMargin?.toFixed(1)}% margin
                </p>
                <p className="text-xs text-accent mt-1">
                  {reportData?.expenses?.accountCount || 0} expense accounts considered
                </p>
              </div>
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-1">Net Income</p>
                <p className={`text-lg font-bold ${
                  reportData?.profitability?.netIncome >= 0 ? 'text-success' : 'text-error'
                }`}>{formatCurrency(reportData?.profitability?.netIncome)}</p>
                <p className="text-xs text-muted-foreground">
                  {reportData?.profitability?.netProfitMargin?.toFixed(1)}% margin
                </p>
                <p className="text-xs text-accent mt-1">
                  {reportData?.accountDetails?.totalAccounts || 0} total P&L accounts
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Data Integration Sources Status */}
        {reportData?.summary?.dataIntegrationSources && (
          <div className="bg-card border border-border rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-semibold text-foreground flex items-center">
                <Icon name="Database" size={18} className="mr-2 text-primary" />
                Data Integration Status
              </h4>
              <div className="text-xs text-muted-foreground">
                Enhanced with actual account values from multiple sources
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {reportData?.summary?.dataIntegrationSources?.map((source, index) => (
                <div key={index} className="bg-muted/5 border border-muted/20 rounded-lg p-3">
                  <div className="flex items-center space-x-2">
                    <Icon 
                      name={source === 'invoices' ? 'FileText' : 
                            source === 'expenses' ? 'Receipt' : 
                            source === 'bills' ? 'FileStack' : 
                            source === 'journal_entries' ? 'BookOpen' : 'Database'} 
                      size={16} 
                      className={`${
                        source === 'invoices' ? 'text-blue' : 
                        source === 'expenses' ? 'text-orange' : 
                        source === 'bills' ? 'text-red' : 
                        source === 'journal_entries' ? 'text-purple' : 'text-gray'
                      }`} 
                    />
                    <span className="text-sm font-medium text-foreground capitalize">
                      {source?.replace('_', ' ')}
                    </span>
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    Integrated for account-level calculations
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Net Income Summary with Account Context */}
        <div className={`rounded-lg p-6 ${
          reportData?.summary?.isProfitable ? 'bg-success/5 border border-success/20' : 'bg-error/5 border border-error/20'
        }`}>
          <div className="text-center">
            <Icon 
              name={reportData?.summary?.isProfitable ? "TrendingUp" : "TrendingDown"} 
              size={32} 
              className={`mx-auto mb-2 ${reportData?.summary?.isProfitable ? 'text-success' : 'text-error'}`} 
            />
            <h4 className={`text-2xl font-bold mb-2 ${
              reportData?.summary?.isProfitable ? 'text-success' : 'text-error'
            }`}>
              Net Income: {formatCurrency(reportData?.summary?.netIncome)}
            </h4>
            <p className={`text-sm font-medium ${
              reportData?.summary?.isProfitable ? 'text-success' : 'text-error'
            }`}>
              {reportData?.summary?.isProfitable ? 'Profitable Period' : 'Loss Period'}
            </p>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Revenue from </span>
                <span className="font-medium text-success">{reportData?.revenue?.accountCount} accounts</span>
              </div>
              <div>
                <span className="text-muted-foreground">Expenses from </span>
                <span className="font-medium text-error">{reportData?.expenses?.accountCount} accounts</span>
              </div>
              <div>
                <span className="text-muted-foreground">Net margin: </span>
                <span className={`font-medium ${
                  reportData?.profitability?.netProfitMargin >= 0 ? 'text-success' : 'text-error'
                }`}>
                  {reportData?.profitability?.netProfitMargin?.toFixed(1)}%
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderBalanceSheet = (reportData) => {
    if (!reportData?.assets && !reportData?.liabilities && !reportData?.equity) return null;

    const renderAccountSection = (title, accounts, isCredit = false) => (
      <div className="space-y-2">
        <h5 className="font-medium text-foreground">{title}</h5>
        {Array.isArray(accounts) && accounts?.map((account, index) => (
          <div key={index} className="flex justify-between text-sm pl-4">
            <span className="text-muted-foreground">{account?.accountCode} - {account?.accountName}</span>
            <span className={`font-medium ${isCredit ? 'text-primary' : 'text-foreground'}`}>
              {formatCurrency(account?.balance)}
            </span>
          </div>
        ))}
      </div>
    );

    return (
      <div className="space-y-6">
        {/* Balance Sheet Header */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-card border border-border rounded-lg p-4">
            <h4 className="text-lg font-semibold text-foreground mb-2">Balance Sheet</h4>
            <p className="text-sm text-muted-foreground">As of {formatDate(reportData?.asOfDate)}</p>
          </div>

          <div className={`border rounded-lg p-4 ${
            reportData?.totals?.isBalanced ? 'bg-success/10 border-success/20' : 'bg-error/10 border-error/20'
          }`}>
            <div className="flex items-center">
              <Icon 
                name={reportData?.totals?.isBalanced ? "CheckCircle" : "AlertCircle"} 
                size={20} 
                className={`mr-2 ${reportData?.totals?.isBalanced ? 'text-success' : 'text-error'}`} 
              />
              <div>
                <p className={`text-sm font-medium ${
                  reportData?.totals?.isBalanced ? 'text-success' : 'text-error'
                }`}>Balance Check</p>
                <p className={`text-lg font-bold ${
                  reportData?.totals?.isBalanced ? 'text-success' : 'text-error'
                }`}>
                  {reportData?.totals?.isBalanced ? 'Balanced' : 'Out of Balance'}
                </p>
                {!reportData?.totals?.isBalanced && (
                  <p className="text-xs text-error mt-1">
                    Difference: {formatCurrency(Math.abs(reportData?.totals?.difference))}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Assets Section */}
        <div className="bg-card border border-border rounded-lg">
          <div className="p-4 border-b border-border">
            <h4 className="text-lg font-semibold text-foreground">Assets</h4>
          </div>
          <div className="p-4 space-y-4">
            {Array.isArray(reportData?.assets?.currentAssets) && reportData?.assets?.currentAssets?.length > 0 && 
              renderAccountSection("Current Assets", reportData?.assets?.currentAssets)}
            {Array.isArray(reportData?.assets?.fixedAssets) && reportData?.assets?.fixedAssets?.length > 0 && 
              renderAccountSection("Fixed Assets", reportData?.assets?.fixedAssets)}
            {Array.isArray(reportData?.assets?.nonCurrentAssets) && reportData?.assets?.nonCurrentAssets?.length > 0 && 
              renderAccountSection("Non-Current Assets", reportData?.assets?.nonCurrentAssets)}
            {Array.isArray(reportData?.assets?.intangibleAssets) && reportData?.assets?.intangibleAssets?.length > 0 && 
              renderAccountSection("Intangible Assets", reportData?.assets?.intangibleAssets)}
            
            <div className="border-t border-border pt-3">
              <div className="flex justify-between items-center font-bold text-lg">
                <span className="text-foreground">Total Assets</span>
                <span className="text-primary">{formatCurrency(reportData?.totals?.totalAssets)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Liabilities Section */}
        <div className="bg-card border border-border rounded-lg">
          <div className="p-4 border-b border-border">
            <h4 className="text-lg font-semibold text-foreground">Liabilities</h4>
          </div>
          <div className="p-4 space-y-4">
            {Array.isArray(reportData?.liabilities?.currentLiabilities) && reportData?.liabilities?.currentLiabilities?.length > 0 && 
              renderAccountSection("Current Liabilities", reportData?.liabilities?.currentLiabilities, true)}
            {Array.isArray(reportData?.liabilities?.longTermDebt) && reportData?.liabilities?.longTermDebt?.length > 0 && 
              renderAccountSection("Long-term Debt", reportData?.liabilities?.longTermDebt, true)}
            {Array.isArray(reportData?.liabilities?.nonCurrentLiabilities) && reportData?.liabilities?.nonCurrentLiabilities?.length > 0 && 
              renderAccountSection("Non-Current Liabilities", reportData?.liabilities?.nonCurrentLiabilities, true)}
            
            <div className="border-t border-border pt-3">
              <div className="flex justify-between items-center font-bold text-lg">
                <span className="text-foreground">Total Liabilities</span>
                <span className="text-error">{formatCurrency(reportData?.totals?.totalLiabilities)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Equity Section */}
        <div className="bg-card border border-border rounded-lg">
          <div className="p-4 border-b border-border">
            <h4 className="text-lg font-semibold text-foreground">Equity</h4>
          </div>
          <div className="p-4 space-y-4">
            {Array.isArray(reportData?.equity?.shareCapital) && reportData?.equity?.shareCapital?.length > 0 && 
              renderAccountSection("Share Capital", reportData?.equity?.shareCapital, true)}
            {Array.isArray(reportData?.equity?.retainedEarnings) && reportData?.equity?.retainedEarnings?.length > 0 && 
              renderAccountSection("Retained Earnings", reportData?.equity?.retainedEarnings, true)}
            {Array.isArray(reportData?.equity?.otherEquity) && reportData?.equity?.otherEquity?.length > 0 && 
              renderAccountSection("Other Equity", reportData?.equity?.otherEquity, true)}
            
            <div className="border-t border-border pt-3">
              <div className="flex justify-between items-center font-bold text-lg">
                <span className="text-foreground">Total Equity</span>
                <span className="text-success">{formatCurrency(reportData?.totals?.totalEquity)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Totals Verification */}
        <div className="bg-muted/10 border border-border rounded-lg p-4">
          <h4 className="text-lg font-semibold text-foreground mb-4">Balance Sheet Equation</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-sm text-muted-foreground">Assets</p>
              <p className="text-xl font-bold text-primary">{formatCurrency(reportData?.totals?.totalAssets)}</p>
            </div>
            <div className="flex items-center justify-center">
              <span className="text-2xl font-bold text-muted-foreground">=</span>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Liabilities + Equity</p>
              <p className="text-xl font-bold text-foreground">{formatCurrency(reportData?.totals?.totalLiabilitiesAndEquity)}</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderInvoiceAging = (reportData) => {
    if (!reportData?.data) return null;

    return (
      <div className="space-y-6">
        {/* Aging Summary */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div className="bg-success/10 border border-success/20 rounded-lg p-4">
            <div className="text-center">
              <p className="text-sm text-success font-medium">0-30 Days</p>
              <p className="text-lg font-bold text-success">{formatCurrency(reportData?.aging?.days0to30)}</p>
            </div>
          </div>
          <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
            <div className="text-center">
              <p className="text-sm text-warning font-medium">31-60 Days</p>
              <p className="text-lg font-bold text-warning">{formatCurrency(reportData?.aging?.days31to60)}</p>
            </div>
          </div>
          <div className="bg-orange/10 border border-orange/20 rounded-lg p-4">
            <div className="text-center">
              <p className="text-sm text-orange font-medium">61-90 Days</p>
              <p className="text-lg font-bold text-orange">{formatCurrency(reportData?.aging?.days61to90)}</p>
            </div>
          </div>
          <div className="bg-red/10 border border-red/20 rounded-lg p-4">
            <div className="text-center">
              <p className="text-sm text-red font-medium">91-120 Days</p>
              <p className="text-lg font-bold text-red">{formatCurrency(reportData?.aging?.days91to120)}</p>
            </div>
          </div>
          <div className="bg-error/10 border border-error/20 rounded-lg p-4">
            <div className="text-center">
              <p className="text-sm text-error font-medium">120+ Days</p>
              <p className="text-lg font-bold text-error">{formatCurrency(reportData?.aging?.days120plus)}</p>
            </div>
          </div>
        </div>

        {/* Invoice Aging Table */}
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/20">
                <tr>
                  <th className="text-left py-3 px-4 font-semibold text-foreground">Invoice #</th>
                  <th className="text-left py-3 px-4 font-semibold text-foreground">Customer</th>
                  <th className="text-left py-3 px-4 font-semibold text-foreground">Issue Date</th>
                  <th className="text-left py-3 px-4 font-semibold text-foreground">Total Amount</th>
                  <th className="text-left py-3 px-4 font-semibold text-foreground">Days Since Issue</th>
                  <th className="text-left py-3 px-4 font-semibold text-foreground">Aging Bucket</th>
                  <th className="text-left py-3 px-4 font-semibold text-foreground">Status</th>
                </tr>
              </thead>
              <tbody>
                {Array.isArray(reportData?.data) && reportData?.data?.map((invoice, index) => (
                  <tr key={index} className="border-b border-border hover:bg-muted/20">
                    <td className="py-3 px-4 font-medium text-foreground">{invoice?.invoiceNumber}</td>
                    <td className="py-3 px-4 text-foreground">{invoice?.customerName}</td>
                    <td className="py-3 px-4 text-muted-foreground">{formatDate(invoice?.issueDate)}</td>
                    <td className="py-3 px-4 text-foreground">{formatCurrency(invoice?.totalAmount)}</td>
                    <td className="py-3 px-4 text-foreground">{invoice?.daysFromIssue} days</td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        invoice?.agingBucket === '0-30 Days' ? 'bg-success/20 text-success' :
                        invoice?.agingBucket === '31-60 Days' ? 'bg-warning/20 text-warning' :
                        invoice?.agingBucket === '61-90 Days' ? 'bg-orange/20 text-orange' :
                        invoice?.agingBucket === '91-120 Days' ? 'bg-red/20 text-red' : 'bg-error/20 text-error'
                      }`}>
                        {invoice?.agingBucket}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        invoice?.status === 'paid' ? 'bg-success/20 text-success' :
                        invoice?.status === 'overdue' ? 'bg-error/20 text-error' : 'bg-warning/20 text-warning'
                      }`}>
                        {invoice?.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderCustomerRevenue = (reportData) => {
    if (!reportData?.data) return null;

    return (
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
            <div className="flex items-center">
              <Icon name="Users" size={20} className="text-primary mr-2" />
              <div>
                <p className="text-sm text-primary font-medium">Total Customers</p>
                <p className="text-lg font-bold text-primary">{reportData?.totals?.totalCustomers}</p>
              </div>
            </div>
          </div>
          <div className="bg-success/10 border border-success/20 rounded-lg p-4">
            <div className="flex items-center">
              <Icon name="DollarSign" size={20} className="text-success mr-2" />
              <div>
                <p className="text-sm text-success font-medium">Total Revenue</p>
                <p className="text-lg font-bold text-success">{formatCurrency(reportData?.totals?.totalRevenue)}</p>
              </div>
            </div>
          </div>
          <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
            <div className="flex items-center">
              <Icon name="TrendingUp" size={20} className="text-accent mr-2" />
              <div>
                <p className="text-sm text-accent font-medium">Avg per Customer</p>
                <p className="text-lg font-bold text-accent">{formatCurrency(reportData?.totals?.averageRevenuePerCustomer)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Customer Revenue Table */}
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/20">
                <tr>
                  <th className="text-left py-3 px-4 font-semibold text-foreground">Customer Name</th>
                  <th className="text-left py-3 px-4 font-semibold text-foreground">Total Revenue</th>
                  <th className="text-left py-3 px-4 font-semibold text-foreground">Invoice Count</th>
                  <th className="text-left py-3 px-4 font-semibold text-foreground">Average Invoice</th>
                </tr>
              </thead>
              <tbody>
                {Array.isArray(reportData?.data) && reportData?.data?.map((customer, index) => (
                  <tr key={index} className="border-b border-border hover:bg-muted/20">
                    <td className="py-3 px-4 font-medium text-foreground">{customer?.customerName}</td>
                    <td className="py-3 px-4 text-success font-medium">{formatCurrency(customer?.totalRevenue)}</td>
                    <td className="py-3 px-4 text-foreground">{customer?.invoiceCount}</td>
                    <td className="py-3 px-4 text-foreground">{formatCurrency(customer?.averageInvoice)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderExpenseCategories = (reportData) => {
    if (!reportData?.data) return null;

    return (
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
            <div className="flex items-center">
              <Icon name="Layers" size={20} className="text-primary mr-2" />
              <div>
                <p className="text-sm text-primary font-medium">Categories</p>
                <p className="text-lg font-bold text-primary">{reportData?.totals?.totalCategories}</p>
              </div>
            </div>
          </div>
          <div className="bg-error/10 border border-error/20 rounded-lg p-4">
            <div className="flex items-center">
              <Icon name="DollarSign" size={20} className="text-error mr-2" />
              <div>
                <p className="text-sm text-error font-medium">Total Expenses</p>
                <p className="text-lg font-bold text-error">{formatCurrency(reportData?.totals?.totalExpenses)}</p>
              </div>
            </div>
          </div>
          <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
            <div className="flex items-center">
              <Icon name="BarChart" size={20} className="text-accent mr-2" />
              <div>
                <p className="text-sm text-accent font-medium">Avg per Category</p>
                <p className="text-lg font-bold text-accent">{formatCurrency(reportData?.totals?.averagePerCategory)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Categories Table */}
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/20">
                <tr>
                  <th className="text-left py-3 px-4 font-semibold text-foreground">Category</th>
                  <th className="text-left py-3 px-4 font-semibold text-foreground">Total Amount</th>
                  <th className="text-left py-3 px-4 font-semibold text-foreground">Expense Count</th>
                  <th className="text-left py-3 px-4 font-semibold text-foreground">Average Expense</th>
                </tr>
              </thead>
              <tbody>
                {Array.isArray(reportData?.data) && reportData?.data?.map((category, index) => (
                  <tr key={index} className="border-b border-border hover:bg-muted/20">
                    <td className="py-3 px-4 font-medium text-foreground capitalize">{category?.category?.replace('_', ' ')}</td>
                    <td className="py-3 px-4 text-error font-medium">{formatCurrency(category?.totalAmount)}</td>
                    <td className="py-3 px-4 text-foreground">{category?.expenseCount}</td>
                    <td className="py-3 px-4 text-foreground">{formatCurrency(category?.averageExpense)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderVendorSpending = (reportData) => {
    if (!reportData?.data) return null;

    return (
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
            <div className="flex items-center">
              <Icon name="Building" size={20} className="text-primary mr-2" />
              <div>
                <p className="text-sm text-primary font-medium">Total Vendors</p>
                <p className="text-lg font-bold text-primary">{reportData?.totals?.totalVendors}</p>
              </div>
            </div>
          </div>
          <div className="bg-error/10 border border-error/20 rounded-lg p-4">
            <div className="flex items-center">
              <Icon name="DollarSign" size={20} className="text-error mr-2" />
              <div>
                <p className="text-sm text-error font-medium">Total Spending</p>
                <p className="text-lg font-bold text-error">{formatCurrency(reportData?.totals?.totalSpending)}</p>
              </div>
            </div>
          </div>
          <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
            <div className="flex items-center">
              <Icon name="TrendingUp" size={20} className="text-accent mr-2" />
              <div>
                <p className="text-sm text-accent font-medium">Avg per Vendor</p>
                <p className="text-lg font-bold text-accent">{formatCurrency(reportData?.totals?.averageSpendingPerVendor)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Vendor Spending Table */}
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/20">
                <tr>
                  <th className="text-left py-3 px-4 font-semibold text-foreground">Vendor Name</th>
                  <th className="text-left py-3 px-4 font-semibold text-foreground">Total Spending</th>
                  <th className="text-left py-3 px-4 font-semibold text-foreground">Expenses</th>
                  <th className="text-left py-3 px-4 font-semibold text-foreground">Bills</th>
                  <th className="text-left py-3 px-4 font-semibold text-foreground">Avg Transaction</th>
                </tr>
              </thead>
              <tbody>
                {Array.isArray(reportData?.data) && reportData?.data?.map((vendor, index) => (
                  <tr key={index} className="border-b border-border hover:bg-muted/20">
                    <td className="py-3 px-4 font-medium text-foreground">{vendor?.vendorName}</td>
                    <td className="py-3 px-4 text-error font-medium">{formatCurrency(vendor?.totalSpending)}</td>
                    <td className="py-3 px-4 text-foreground">{vendor?.expenseCount}</td>
                    <td className="py-3 px-4 text-foreground">{vendor?.billCount}</td>
                    <td className="py-3 px-4 text-foreground">{formatCurrency(vendor?.averageTransaction)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderPaymentTrends = (reportData) => {
    if (!reportData?.data) return null;

    return (
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
            <div className="flex items-center">
              <Icon name="Calendar" size={20} className="text-primary mr-2" />
              <div>
                <p className="text-sm text-primary font-medium">Months Tracked</p>
                <p className="text-lg font-bold text-primary">{reportData?.summary?.totalMonths}</p>
              </div>
            </div>
          </div>
          <div className="bg-success/10 border border-success/20 rounded-lg p-4">
            <div className="flex items-center">
              <Icon name="ArrowUp" size={20} className="text-success mr-2" />
              <div>
                <p className="text-sm text-success font-medium">Total Received</p>
                <p className="text-lg font-bold text-success">{formatCurrency(reportData?.summary?.totalReceived)}</p>
              </div>
            </div>
          </div>
          <div className="bg-error/10 border border-error/20 rounded-lg p-4">
            <div className="flex items-center">
              <Icon name="ArrowDown" size={20} className="text-error mr-2" />
              <div>
                <p className="text-sm text-error font-medium">Total Made</p>
                <p className="text-lg font-bold text-error">{formatCurrency(reportData?.summary?.totalMade)}</p>
              </div>
            </div>
          </div>
          <div className={`border rounded-lg p-4 ${
            reportData?.summary?.averageMonthlyNet >= 0 ? 'bg-accent/10 border-accent/20' : 'bg-warning/10 border-warning/20'
          }`}>
            <div className="flex items-center">
              <Icon name="TrendingUp" size={20} className={`mr-2 ${
                reportData?.summary?.averageMonthlyNet >= 0 ? 'text-accent' : 'text-warning'
              }`} />
              <div>
                <p className={`text-sm font-medium ${
                  reportData?.summary?.averageMonthlyNet >= 0 ? 'text-accent' : 'text-warning'
                }`}>Avg Monthly Net</p>
                <p className={`text-lg font-bold ${
                  reportData?.summary?.averageMonthlyNet >= 0 ? 'text-accent' : 'text-warning'
                }`}>{formatCurrency(reportData?.summary?.averageMonthlyNet)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Monthly Trends Table */}
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/20">
                <tr>
                  <th className="text-left py-3 px-4 font-semibold text-foreground">Month</th>
                  <th className="text-left py-3 px-4 font-semibold text-foreground">Payments Received</th>
                  <th className="text-left py-3 px-4 font-semibold text-foreground">Payments Made</th>
                  <th className="text-left py-3 px-4 font-semibold text-foreground">Net Cash Flow</th>
                </tr>
              </thead>
              <tbody>
                {Array.isArray(reportData?.data) && reportData?.data?.map((trend, index) => (
                  <tr key={index} className="border-b border-border hover:bg-muted/20">
                    <td className="py-3 px-4 font-medium text-foreground">{trend?.month}</td>
                    <td className="py-3 px-4 text-success font-medium">{formatCurrency(trend?.received)}</td>
                    <td className="py-3 px-4 text-error font-medium">{formatCurrency(trend?.made)}</td>
                    <td className="py-3 px-4">
                      <span className={`font-medium ${
                        trend?.net >= 0 ? 'text-success' : 'text-error'
                      }`}>
                        {formatCurrency(trend?.net)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderReportContent = () => {
    if (!data) return null;

    // Handle placeholder reports
    if (data?.isPlaceholder) {
      return (
        <div className="text-center py-12">
          <Icon name="Construction" size={64} className="text-warning mx-auto mb-4" />
          <h3 className="text-xl font-medium text-foreground mb-2">{data?.title}</h3>
          <p className="text-muted-foreground mb-4">{data?.message}</p>
          <div className="bg-warning/5 border border-warning/20 rounded-lg p-4 max-w-md mx-auto">
            <p className="text-sm text-warning">
              This report type ({data?.reportId}) is currently under development and will be available in the next update.
            </p>
          </div>
        </div>
      );
    }

    switch (selectedReport?.id) {
      case 'sales-summary':
        return renderSalesSummary(data);
      case 'expense-summary':
        return renderExpenseSummary(data);
      case 'cash-flow-statement':
        return renderCashFlowStatement(data);
      case 'outstanding-balances':
        return renderOutstandingBalances(data);
      case 'trial-balance':
        return renderTrialBalance(data);
      case 'profit-loss':
        return renderProfitLossStatement(data);
      case 'balance-sheet':
        return renderBalanceSheet(data);
      case 'invoice-aging':
        return renderInvoiceAging(data);
      case 'customer-revenue':
        return renderCustomerRevenue(data);
      case 'expense-categories':
        return renderExpenseCategories(data);
      case 'vendor-spending':
        return renderVendorSpending(data);
      case 'payment-trends':
        return renderPaymentTrends(data);
      default:
        return (
          <div className="text-center py-8">
            <Icon name="FileQuestion" size={48} className="text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">Report Viewer</h3>
            <p className="text-muted-foreground">
              Report viewer for "{selectedReport?.name}" is being developed.
            </p>
          </div>
        );
    }
  };

  if (!selectedReport) {
    return (
      <div className="bg-card border border-border rounded-lg p-8">
        <div className="text-center">
          <Icon name="FileText" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No Report Selected</h3>
          <p className="text-muted-foreground">
            Select a report from the categories or recent reports to view its content.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-8">
        <div className="text-center">
          <Icon name="Loader2" size={48} className="text-primary mx-auto mb-4 animate-spin" />
          <h3 className="text-lg font-medium text-foreground mb-2">Loading Report</h3>
          <p className="text-muted-foreground">
            Generating "{selectedReport?.name}" report...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card border border-error/20 rounded-lg p-8">
        <div className="text-center">
          <Icon name="AlertCircle" size={48} className="text-error mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">Error Loading Report</h3>
          <p className="text-muted-foreground mb-4">{error}</p>
          <Button 
            variant="outline" 
            onClick={loadReportData}
            iconName="RefreshCw"
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg">
      {/* Report Header */}
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-foreground">{data?.title || selectedReport?.name}</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Generated on {new Date()?.toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handlePrint}
              iconName="Printer"
            >
              Print
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleShare}
              iconName="Share"
            >
              Share
            </Button>
            <div className="relative">
              <select
                value={exportFormat}
                onChange={(e) => setExportFormat(e?.target?.value)}
                className="appearance-none bg-background border border-border rounded px-3 py-1 text-sm pr-8"
              >
                <option value="pdf">PDF</option>
                <option value="excel">Excel</option>
                <option value="csv">CSV</option>
              </select>
              <Icon name="ChevronDown" size={16} className="absolute right-2 top-1/2 transform -translate-y-1/2 text-muted-foreground pointer-events-none" />
            </div>
            <Button
              size="sm"
              onClick={() => handleExport(exportFormat)}
              iconName="Download"
            >
              Export
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={loadReportData}
              iconName="RefreshCw"
            >
              Refresh
            </Button>
          </div>
        </div>
      </div>
      {/* Report Content */}
      <div className="p-6">
        {renderReportContent()}
      </div>
    </div>
  );
};

export default ReportViewer;